import React, { useState } from 'react';
import moment from 'moment';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import AddNewCardModal from './Modal/AddNewCard';
import PrimaryCardModal from './Modal/PrimaryCard';
import {
  Chip,
  Button,
  Tooltip,
  IconButton,
  Grid,
  CircularProgress
} from '@mui/material';
import { CloseIcon } from '@/common/icons/CloseIcon';
import trash2Fill from '@iconify-icons/eva/trash-2-fill';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { makeStyles } from '@mui/styles';
import { GridColumns, GridSortModel } from '@mui/x-data-grid';

import { MIconButton } from '../../../../../../../@material-extend';
import DeleteModal from '../../../../../../../components/DeleteModal';
import { Alert } from '@/RestoreComponent/Alert';
import { paymentMethodId2Gql } from '@/common/utils/apiConversionUtils';
import {
  useGetPaymentMethods,
  useDeletePaymentMethod,
  useUpdatePrimaryPaymentMethod
} from '@/api/finance/staff';
import { StaffGetPaymentMethodsListResponse } from '@/api/finance/finance-api.schemas';
import { RestoreDataGrid } from '@/RestoreComponent/DataGrid';

const PAYMENT_METHOD_ERROR_MESSAGES: Record<string, string> = {
  ACTIVE_MEMBERSHIPS: 'Default card required for membership autopay'
};

interface PaymentMethod {
  id: string;
  paymentMethodId: number;
  paymentType: string;
  last4: string;
  expires: number;
  nickname: string | null;
  issuer: string;
  processor: string;
  token: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  store: unknown;
}

const useStyles: any = makeStyles(() => ({
  Chip: {
    '&.MuiChip-icon': {
      marginLeft: 0,
      width: '20px',
      height: '20px',
      padding: '3px',
      marginRight: '10px'
    }
  }
}));

export default function SavedCards(props: any) {
  const classes = useStyles();
  const { customerAllData } = props;

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortModel] = useState<GridSortModel>([]);
  const [selectedItem, setSelectedItem] = useState<PaymentMethod | null>(null);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPrimaryModalOpen, setIsPrimaryModalOpen] = useState(false);
  const [isDeleteButtonPressed, setIsDeleteButtonPressed] = useState(false);
  const [isCreditsDetailsModalOpen, setIsCreditsDetailsModalOpen] =
    useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const {
    data: cardsData,
    isLoading: cardsloading,
    refetch: refetchCards
  } = useGetPaymentMethods(
    { customerId: customerAllData?.id },
    { query: { enabled: !!customerAllData?.id } }
  );

  const {
    data: defaultPaymentData,
    isLoading,
    refetch: refetchDefaultPayment
  } = useGetPaymentMethods(
    { customerId: customerAllData?.id, defaultPaymentOnly: true },
    { query: { enabled: !!customerAllData?.id } }
  );

  const allCards = React.useMemo<PaymentMethod[]>(() => {
    return (
      cardsData?.data?.map((card: StaffGetPaymentMethodsListResponse) => ({
        id: paymentMethodId2Gql(card.paymentMethodId) || '',
        paymentMethodId: card.paymentMethodId,
        paymentType: card.paymentType,
        last4: card.last4,
        expires: card.expires,
        nickname: card.nickname,
        issuer: card.issuer,
        processor: card.processor,
        token: card.token,
        address: card.address,
        city: card.city,
        state: card.state,
        zip: card.zip,
        store: card.store
      })) || []
    );
  }, [cardsData]);

  const defaultPayment = React.useMemo(() => {
    const defaultCard = defaultPaymentData?.data?.[0];
    return defaultCard
      ? paymentMethodId2Gql(defaultCard.paymentMethodId)
      : null;
  }, [defaultPaymentData]);

  const { mutateAsync: deletePaymentMethodMutation } = useDeletePaymentMethod();
  const { mutateAsync: updatePrimaryPaymentMethodMutation } =
    useUpdatePrimaryPaymentMethod();

  const updateDefaultPaymentAfterDelete = async (deletedId: string) => {
    const remainingCards = allCards?.filter(c => c.id !== deletedId);
    if (remainingCards?.length === 1 && remainingCards[0]?.paymentMethodId) {
      await updatePrimaryPaymentMethodMutation({
        id: remainingCards[0].paymentMethodId
      });
      refetchDefaultPayment();
    }
  };

  const handleDeleteModal = (item: PaymentMethod) => {
    setSelectedItem(item);
    setIsDeleteModalOpen(!isDeleteModalOpen);
  };

  const confirmDelete = async () => {
    if (!selectedItem?.paymentMethodId) {
      setErrors(['No payment method selected.']);
      return;
    }
    try {
      const deletedId = selectedItem.id;
      await deletePaymentMethodMutation({ id: selectedItem.paymentMethodId });
      setIsDeleteModalOpen(false);
      setIsDeleteButtonPressed(false);
      enqueueSnackbar('Saved Card Deleted Succesfully!', {
        variant: 'success',
        action: (key: any) => (
          <MIconButton size="small" onClick={() => closeSnackbar(key)}>
            <CloseIcon />
          </MIconButton>
        )
      });
      await updateDefaultPaymentAfterDelete(deletedId);
      refetchCards();
    } catch (e) {
      setIsDeleteModalOpen(false);
      setIsDeleteButtonPressed(false);
      const errorCode = (e as any)?.response?.data?.error_code;
      const errorMessage =
        (errorCode && PAYMENT_METHOD_ERROR_MESSAGES[errorCode]) ||
        'Error deleting card.';
      setErrors([errorMessage]);
    }
  };

  const handleCardModalOpen = () => {
    setIsPrimaryModalOpen(true);
  };

  const rows = allCards.slice(page * pageSize, (page + 1) * pageSize);

  const columnConfig: GridColumns = [
    {
      field: 'paymentType',
      headerName: 'Payment Type',
      flex: 1,
      sortable: false
    },
    {
      field: 'last4',
      headerName: 'Last 4',
      flex: 0.7,
      sortable: false
    },
    {
      field: 'expires',
      headerName: 'Expires',
      flex: 0.8,
      sortable: false,
      renderCell: ({ row }) => moment(row.expires).utc().format('MMMM YYYY')
    },
    {
      field: 'nickname',
      headerName: 'Nickname',
      flex: 1,
      sortable: false
    },
    {
      field: 'primaryCard',
      headerName: 'Primary Card',
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) =>
        row.id !== defaultPayment ? (
          <div>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <Chip
                disabled={row.id === defaultPayment}
                color={row.id === defaultPayment ? 'secondary' : 'primary'}
                variant="filled"
                icon={
                  <CreditCardIcon fontSize="small" className={classes.Chip} />
                }
                onClick={() => {
                  setSelectedItem(row);
                  handleCardModalOpen();
                }}
                label="Make this the Default Card"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'row-reverse'
                }}
              />
            )}
          </div>
        ) : (
          <div
            style={{
              color: '#008000',
              fontWeight: 800,
              textAlign: 'center'
            }}
          >
            Default Card
            <CreditCardIcon fontSize="small" style={{ marginLeft: '10px' }} />
          </div>
        )
    },
    {
      field: '_action',
      headerName: 'Action',
      flex: 0.5,
      sortable: false,
      renderCell: ({ row }) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around'
          }}
        >
          <Tooltip arrow title="Delete">
            <IconButton size="small" onClick={() => handleDeleteModal(row)}>
              <Icon icon={trash2Fill} color="#055577" />
            </IconButton>
          </Tooltip>
        </div>
      )
    }
  ];

  return (
    <>
      <Alert
        onClose={() => setErrors([])}
        open={!!errors?.length}
        messages={errors}
        variant="error"
        title="Delete Payment Method"
      />
      <Grid container spacing={3}>
        <Grid item xs={12} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            startIcon={<VisibilityIcon />}
            onClick={() => setIsCreditsDetailsModalOpen(true)}
          >
            Add New Card
          </Button>
        </Grid>
        <Grid item xs={12}>
          <RestoreDataGrid
            rowCount={allCards.length}
            columnConfig={columnConfig}
            rows={rows}
            loading={cardsloading}
            page={page}
            pageSize={pageSize}
            pageSizes={[10, 50, 100]}
            sortModel={sortModel}
            onPageChange={p => setPage(p)}
            onPageChangeSize={ps => {
              setPageSize(ps);
              setPage(0);
            }}
            onSortModelChange={() => {}}
          />
        </Grid>
      </Grid>
      {isPrimaryModalOpen && (
        <PrimaryCardModal
          cardData={allCards}
          refreshCardList={refetchCards}
          selectedItem={selectedItem as any}
          customerAllData={customerAllData}
          setIsModalOpen={setIsPrimaryModalOpen}
          setDefaultGateway={refetchDefaultPayment}
        />
      )}
      {selectedItem && (
        <DeleteModal
          item={selectedItem}
          open={isDeleteModalOpen}
          confirmDelete={confirmDelete}
          setModal={setIsDeleteModalOpen}
          deleteButtonPressed={isDeleteButtonPressed}
          handleDeleteButtonPressed={setIsDeleteButtonPressed}
        />
      )}
      {isCreditsDetailsModalOpen && (
        <AddNewCardModal
          open={isCreditsDetailsModalOpen}
          setModal={setIsCreditsDetailsModalOpen}
          customer={customerAllData}
          refreshCardList={refetchCards}
          getDefaultPayment={refetchDefaultPayment}
        />
      )}
    </>
  );
}
